import React from "react";
import "./EditScriptDialog.css";
import { withTranslation } from "react-i18next";
import { Accordion, Alert, Button, Card, Col, Container, Form, Row } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExclamationCircle } from "@fortawesome/free-solid-svg-icons";
import MarkDownEditor from "../../../../components/markdownEditor/MarkDownEditor";
import ValidationHelper from "../../../../helpers/ValidationHelper";
import CamplaCoreService from "../../../../services/CamplaCoreService";
import { languages } from "prismjs/components/prism-core";
import BaseDialog from "../../../../components/dialogs/baseDialog/BaseDialog";
import BaseSelect from "../../../../components/baseSelect/BaseSelect";

class EditScriptDialog extends React.Component {
  options = [
    { label: "Powershell Script", value: 0 },
    { label: "Shell (Bash) Script", value: 1 }
  ];

  constructor(props) {
    super(props);
    this.header = React.createRef();
    this.validationDefinition = {
      name: {
        defaultValue: "",
        initValid: true,
        rule: value => ValidationHelper.isNonEmptyStringOfLength(value, 3, 50)
      },
      description: {
        defaultValue: ValidationHelper.getDefaultValueExtendedDescription(),
        initValid: true,
        rule: ValidationHelper.isNonEmptyExtendedDescription
      },
      type: {
        defaultValue: undefined,
        initValid: true,
        rule: value => ValidationHelper.isIntInRange(value, 0, 1)
      },
      script: {
        defaultValue: "",
        initValid: true,
        rule: ValidationHelper.isNonEmptyString
      }
    };

    this.state = {
      apiError: "",
      dialogApiError: "",
      validation: ValidationHelper.getValidation(this.validationDefinition)
    };

    this.handleChange = this.handleChange.bind(this);
    this.refreshValidation = this.refreshValidation.bind(this);
    this.onHide = this.onHide.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
  }

  handleChange(event) {
    this.setState({
      validation: Object.assign(
        {},
        ValidationHelper.update(event, this.state.validation)
      )
    });
  }

  getHiglightingLanguage(selectedLanguage) {
    switch (selectedLanguage) {
      case 0:
        return languages.bash;
      case 1:
      default:
        return languages.powershell;
    }
  }

  refreshValidation() {
    const validationDefinition = {
      name: {
        defaultValue: "" + this.props.script.name,
        initValid: true,
        rule: value => ValidationHelper.isNonEmptyStringOfLength(value, 3, 50)
      },
      description: {
        defaultValue: Object.assign({}, this.props.script.description),
        initValid: true,
        rule: ValidationHelper.isNonEmptyExtendedDescription
      },
      type: {
        defaultValue: 0 + this.props.script.type,
        initValid: true,
        rule: value => ValidationHelper.isIntInRange(value, 0, 1)
      },
      script: {
        defaultValue: atob(this.props.script.script),
        initValid: true,
        rule: ValidationHelper.isNonEmptyString
      }
    };
    this.setState({
      validation: ValidationHelper.getValidation(
        validationDefinition
      )
    });
  }

  componentDidMount() {
    this.refreshValidation();
  }

  async onSubmit() {
    if (this.state.validation._isValid) {
      const validatedData = ValidationHelper.getData(this.state.validation);
      validatedData["userId"] = CamplaCoreService.getAuthenticatedUser()._id;
      //this.header.current.scrollIntoView();
      await this.props.onSubmit(validatedData);
      this.refreshValidation();
    }
  }

  onHide() {
    this.props.onHide(
      ValidationHelper.hasChanged(this.state.validation),
      this.refreshValidation
    );
  }

  render() {
    const { t } = this.props;

    return (
      <BaseDialog
        show={this.props.show}
        onHide={this.onHide}
        title={t("SingleScript.EditModal.Title")}
        dialogApiError={this.props.dialogApiError}
        leftButtonCallback={this.onHide}
        leftButtonText={t("DialogCancel")}
        leftButtonVariant="black"
        rightButtonCallback={this.onSubmit}
        rightButtonText={t("SingleScript.EditModal.Form.Submit")}
        rightButtonIsLoading={this.props.isSaving}
        rightButtonVariant="green"
        rightButtonDisabled={
          !this.state.validation._isValid ||
          !ValidationHelper.hasChanged(this.state.validation) ||
          this.props.networkProfile?.isLocked ||
          this.props.isSaving
        }
        body={(<>{this.props.validationResult && (
          <Row>
            <Col>
              <Alert variant={"danger"}>{this.props.validationResult}</Alert>
            </Col>
          </Row>
        )}
        <Container className="pl-0 pr-0">
          {this.state.apiError ? (
            <Row>
              <Col>{this.state.apiError}</Col>
            </Row>
          ) : (
            ""
          )}
          <Form onSubmit={this.onSubmit}>
            <Accordion defaultActiveKey="0">
              <Card className={"accordion-top-card"}>
                <Card.Header className="pt-0 pb-0 pl-2 pr-2 custom-card-header">
                  <Accordion.Toggle
                    as={Button}
                    variant="link"
                    className="acc-link"
                    eventKey="0"
                  >
                    <FontAwesomeIcon
                      icon={faExclamationCircle}
                      className={
                        this.state.validation.name.isValid &&
                        this.state.validation.type.isValid
                          ? "display-none"
                          : "mr-2 color-red"
                      }
                    />
                    <span>
                      {t("SingleScript.EditModal.Form.ToggleBasic")}
                    </span>
                  </Accordion.Toggle>
                </Card.Header>
                <Accordion.Collapse eventKey="0">
                  <Card.Body>
                    <Form.Group controlId="formScriptName">
                      <Form.Label>
                        {t("SingleScript.EditModal.Form.ScriptName")}
                      </Form.Label>
                      <Form.Control
                        type="text"
                        name="name"
                        placeholder={t(
                          "SingleScript.EditModal.Form.ScriptNamePlaceholder"
                        )}
                        value={this.state.validation.name.value}
                        isInvalid={!this.state.validation.name.isValid}
                        onChange={this.handleChange}
                      />
                      <Form.Control.Feedback type="invalid">
                        {t(
                          "SingleScript.EditModal.Form.ValidationError.Name"
                        )}
                      </Form.Control.Feedback>
                    </Form.Group>
                    <Form.Group controlId="formSwitchIsPublished">
                      <Form.Label>
                        {t("SingleScript.EditModal.Form.Type")}
                      </Form.Label>
                      <BaseSelect
                        options={this.options}
                        menuPosition={"fixed"}
                        placeholder={t(
                          "SingleScript.EditModal.Form.TypePlaceholder"
                        )}
                        onChange={value => {
                          this.handleChange({
                            target: {
                              name: "type",
                              value: value.value
                            }
                          });
                        }}
                        value={this.options
                          .filter(
                            o => o.value === this.state.validation.type.value
                          )
                          .shift()}
                      />
                      <Form.Control
                        type="hidden"
                        isInvalid={!this.state.validation.type.isValid}
                      />
                      <Form.Control.Feedback type="invalid">
                        {t(
                          "SingleScript.EditModal.Form.ValidationError.Type"
                        )}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Card.Body>
                </Accordion.Collapse>
              </Card>
            </Accordion>
            <Accordion defaultActiveKey="1">
              <Card className={"accordion-mid-card"}>
                <Card.Header className="pt-0 pb-0 pl-2 pr-2 custom-card-header">
                  <Accordion.Toggle
                    as={Button}
                    variant="link"
                    className="acc-link"
                    eventKey="1"
                  >
                    <FontAwesomeIcon
                      icon={faExclamationCircle}
                      className={
                        this.state.validation.description.isValid
                          ? "display-none"
                          : "mr-2 color-red"
                      }
                    />
                    <span>
                      {t("SingleScript.EditModal.Form.Description")}
                    </span>
                  </Accordion.Toggle>
                </Card.Header>
                <Accordion.Collapse eventKey="1">
                  <Card.Body>
                    <Form.Group>
                      <MarkDownEditor
                        defaultValue={this.props.script ?
                            this.props.script.description : ValidationHelper.getDefaultValueExtendedDescription()}
                        onChange={value =>
                          this.handleChange({
                            target: {
                              name: "description",
                              value: value
                            }
                          })
                        }
                      />
                      <Form.Control
                        type="hidden"
                        isInvalid={!this.state.validation.description.isValid}
                      />
                      <Form.Control.Feedback type="invalid">
                        {t(
                          "SingleScript.EditModal.Form.ValidationError.Description"
                        )}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Card.Body>
                </Accordion.Collapse>
              </Card>
            </Accordion>
          </Form>
        </Container></>)}
      />
    );
  }
}

export default withTranslation()(EditScriptDialog);
